import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useTeamTrialStatus } from 'hooks/useTeamTrialStatus';
import { findStripeCustomerForTeam } from 'utils/stripeCustomer';

const BoltIcon: React.FC = () => (
  <svg
    width='20'
    height='20'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth={2}
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    <path d='M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z' />
  </svg>
);

const getTrialEndLabel = (cur_user: any): string | null => {
  const teamName = cur_user?.selected_team?.name;
  const customer = findStripeCustomerForTeam(cur_user?.stripe_cus_info, teamName);
  const subscriptions =
    customer?.data?.subscriptions?.data || customer?.subscriptions?.data || [];
  const trialingSub = subscriptions.find((sub: any) => sub.status === 'trialing');
  return trialingSub?.trial_end
    ? moment.unix(trialingSub.trial_end).format('MMM D, YYYY')
    : null;
};

interface TrialNoticeBannerProps {
  cur_user: any;
}

const TrialNoticeBanner: React.FC<TrialNoticeBannerProps> = ({ cur_user }) => {
  const trialStatus = useTeamTrialStatus(cur_user);
  const groupId = cur_user?.selected_team?.id ?? '';
  const storageKey = `trial-notice-banner-dismissed:${groupId}`;
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(storageKey) === 'true';
    } catch {
      return false;
    }
  });

  if (trialStatus.isLoading || trialStatus.status !== 'trialing' || dismissed) {
    return null;
  }

  const trialEndLabel = getTrialEndLabel(cur_user);

  const handleDismiss = () => {
    try {
      localStorage.setItem(storageKey, 'true');
    } catch {}
    setDismissed(true);
  };

  return (
    <div className='bdb-status-reset-banner' role='status'>
      <div className='bdb-status-reset-banner-text trial-notice-banner-text'>
        <div className='trial-notice-banner-plan-col'>
          <span className='wizard-trial-ready-bolt'>
            <BoltIcon />
          </span>
          <span className='wizard-trial-ready-plan-name'>Business</span>
          <span className='msp-inline-active-badge'>Free Trial</span>
        </div>
        <div className='trial-notice-banner-desc-col'>
          Free 3-day trial{trialEndLabel ? ` until ${trialEndLabel}` : ''}.
          Includes 1 free run each of Import, Export and Beneficial
          Ownership.
        </div>
      </div>
      <div className='bdb-status-reset-banner-actions'>
        <Link
          className='btn-primary trial-notice-banner-upgrade-btn'
          to={{ pathname: '/profile', state: { linkTab: 'Subscriptions' } }}
        >
          Upgrade plan <ArrowForwardIcon fontSize='small' />
        </Link>
        <button
          type='button'
          aria-label='Dismiss'
          className='bdb-status-reset-banner-dismiss'
          onClick={handleDismiss}
        >
          <svg
            width='18'
            height='18'
            viewBox='0 0 24 24'
            aria-hidden='true'
            style={{ fill: 'currentColor', flexShrink: 0 }}
          >
            <path d='M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z' />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default TrialNoticeBanner;
